import { useState } from 'react';
import { FaUser, FaLock } from 'react-icons/fa';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../config/firebase';

export default function Login({ setIsAuthenticated }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [nome, setNome] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setIsLoading(true);
  
    try {
      // Autenticar com Firebase
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const idToken = await userCredential.user.getIdToken();

      // Verificar token no backend e obter JWT
      const response = await fetch(`${process.env.REACT_APP_API_URL_USER}/api/auth/verify-token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ idToken }),
      });

      // Verificar se a resposta é JSON
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        console.error("Resposta não é JSON:", text);
        throw new Error("Servidor retornou uma resposta inválida. Verifique se o backend está rodando corretamente.");
      }
  
      const data = await response.json();
  
      if (response.ok) {
        if (data.token) {
          localStorage.setItem("token", data.token);
          localStorage.setItem('isAuthenticated', 'true');
          // Salvar email do usuário para uso nos componentes
          if (data.user && data.user.email) {
            localStorage.setItem('userEmail', data.user.email);
          } else {
            localStorage.setItem('userEmail', email);
          }
          setIsAuthenticated(true);
          alert("Login bem-sucedido!");
        } else {
          setErrorMessage("Token não foi retornado pelo servidor.");
        }
      } else {
        setErrorMessage(data.message || "Erro ao fazer login");
      }
    } catch (error) {
      console.error("Erro no login:", error);
      if (error.code === 'auth/user-not-found') {
        setErrorMessage("Usuário não encontrado. Crie uma conta primeiro.");
      } else if (error.code === 'auth/wrong-password') {
        setErrorMessage("Senha incorreta.");
      } else if (error.code === 'auth/invalid-email') {
        setErrorMessage("Email inválido.");
      } else {
        setErrorMessage(error.message || "Erro ao fazer login");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setIsLoading(true);

    try {
      // Criar usuário no Firebase
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const idToken = await userCredential.user.getIdToken();

      // Registrar no backend
      const response = await fetch(`${process.env.REACT_APP_API_URL_USER}/api/auth/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ idToken, nome }),
      });

      // Verificar se a resposta é JSON
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await response.text();
        console.error("Resposta não é JSON:", text);
        throw new Error("Servidor retornou uma resposta inválida. Verifique se o backend está rodando corretamente.");
      }

      const data = await response.json();

      if (response.ok || response.status === 200 || response.status === 201) {
        if (data.token) {
          localStorage.setItem("token", data.token);
          localStorage.setItem('isAuthenticated', 'true');
          // Salvar email do usuário para uso nos componentes
          if (data.user && data.user.email) {
            localStorage.setItem('userEmail', data.user.email);
          } else {
            localStorage.setItem('userEmail', email);
          }
          setIsAuthenticated(true);
          
          // Mensagem diferente se usuário já existia
          if (data.message && data.message.includes("já existe")) {
            alert("Login realizado com sucesso! Sua conta já existia.");
          } else {
            alert("Conta criada com sucesso!");
          }
        } else {
          setErrorMessage("Token não foi retornado pelo servidor.");
        }
      } else {
        setErrorMessage(data.message || "Erro ao criar conta");
      }
    } catch (error) {
      console.error("Erro no registro:", error);
      
      // Se o erro é que o email já está em uso no Firebase, tentar fazer login
      if (error.code === 'auth/email-already-in-use') {
        try {
          // Tentar fazer login com as credenciais
          const loginCredential = await signInWithEmailAndPassword(auth, email, password);
          const loginToken = await loginCredential.user.getIdToken();
          
          // Verificar token no backend
          const loginResponse = await fetch(`${process.env.REACT_APP_API_URL_USER}/api/auth/verify-token`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ idToken: loginToken }),
          });

          const loginContentType = loginResponse.headers.get("content-type");
          if (!loginContentType || !loginContentType.includes("application/json")) {
            const text = await loginResponse.text();
            console.error("Resposta não é JSON:", text);
            setErrorMessage("Conta já existe. Tente fazer login.");
            return;
          }

          const loginData = await loginResponse.json();
          
          if (loginResponse.ok && loginData.token) {
            localStorage.setItem("token", loginData.token);
            localStorage.setItem('isAuthenticated', 'true');
            // Salvar email do usuário
            if (loginData.user && loginData.user.email) {
              localStorage.setItem('userEmail', loginData.user.email);
            } else {
              localStorage.setItem('userEmail', email);
            }
            setIsAuthenticated(true);
            alert("Login realizado com sucesso! Sua conta já existia.");
            return;
          }
        } catch (loginError) {
          console.error("Erro ao tentar fazer login:", loginError);
        }
        setErrorMessage("Este email já está em uso. Tente fazer login.");
      } else if (error.code === 'auth/weak-password') {
        setErrorMessage("Senha muito fraca. Use pelo menos 6 caracteres.");
      } else if (error.code === 'auth/invalid-email') {
        setErrorMessage("Email inválido.");
      } else {
        setErrorMessage(error.message || "Erro ao criar conta");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md bg-white rounded-xlarge shadow-2xl p-8 space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-primary-600 mb-4">
            {isRegistering ? 'Criar Conta' : 'Bem-vindo'}
          </h2>
          <p className="text-gray-600 mb-6">
            {isRegistering ? 'Crie sua conta para continuar' : 'Faça login para continuar'}
          </p>
        </div>
        
        {errorMessage && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
            {errorMessage}
          </div>
        )}
        
        <form onSubmit={isRegistering ? handleRegister : handleLogin} className="space-y-4">
          {isRegistering && (
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaUser className="text-gray-400" />
              </div>
              <input 
                type="text" 
                placeholder="Nome"
                value={nome} 
                onChange={(e) => setNome(e.target.value)} 
                required 
                className="w-full pl-10 pr-4 py-3 bg-white text-gray-900 placeholder-gray-400 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:bg-white focus:text-gray-900 transition duration-300"
              />
            </div>
          )}

          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaUser className="text-gray-400" />
            </div>
            <input 
              type="email" 
              placeholder="Email"
              value={email} 
              onChange={(e) => setEmail(e.target.value)} 
              required 
              className="w-full pl-10 pr-4 py-3 bg-white text-gray-900 placeholder-gray-400 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:bg-white focus:text-gray-900 transition duration-300"
            />
          </div>
          
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FaLock className="text-gray-400" />
            </div>
            <input 
              type="password" 
              placeholder="Senha"
              value={password} 
              onChange={(e) => setPassword(e.target.value)} 
              required 
              minLength={6}
              className="w-full pl-10 pr-4 py-3 bg-white text-gray-900 placeholder-gray-400 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:bg-white focus:text-gray-900 transition duration-300"
            />
          </div>
          
          <button 
            type="submit" 
            disabled={isLoading}
            className="w-full bg-primary-500 text-white py-3 rounded-lg hover:bg-primary-600 transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Carregando...' : (isRegistering ? 'Criar Conta' : 'Entrar')}
          </button>
        </form>
        
        <div className="text-center mt-4 space-y-2">
          <button
            type="button"
            onClick={() => {
              setIsRegistering(!isRegistering);
              setErrorMessage('');
              setNome('');
              setEmail('');
              setPassword('');
            }}
            className="text-sm text-primary-600 hover:underline"
          >
            {isRegistering ? 'Já tem uma conta? Faça login' : 'Não tem uma conta? Criar conta'}
          </button>
          
          {!isRegistering && (
            <div>
              <a href="/recuperar-senha" className="text-sm text-primary-600 hover:underline">
                Esqueceu sua senha?
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
